import {DRAFT_PROJECT_ID_KEY} from '@contentlab/config/constants';
import {FeatureName} from '@contentlab/types/project';
import getLtid from './getLtid';

function setLocalStorageWithLtid(key: string, value: any) {
  const ltId = getLtid();
  if (ltId) {
    localStorage.setItem(`${key}_${ltId}`, value);
  }
}

function getLocalStorageWithLtid(key: string) {
  const ltId = getLtid();
  if (ltId) {
    return localStorage.getItem(`${key}_${ltId}`);
  }
  return null;
}

function removeLocalStorageWithLtid(key: string) {
  const ltId = getLtid();
  if (ltId) {
    localStorage.removeItem(`${key}_${ltId}`);
  }
}

const ONBOARDING_COMPLETED_KEY = 'onboarding_completed_v1';
export function setContentStudioOnboardingCompleted() {
  setLocalStorageWithLtid(ONBOARDING_COMPLETED_KEY, true);
}

export function getContentStudioOnboardingCompleted() {
  return getLocalStorageWithLtid(ONBOARDING_COMPLETED_KEY);
}

const ONBOARDING_PRICING_PAGE_VISITED_KEY =
  'onboarding_pricing_page_visited_v1';
export function setContentLabOnboardingPricingPageVisited() {
  setLocalStorageWithLtid(ONBOARDING_PRICING_PAGE_VISITED_KEY, true);
}

export function getContentLabOnboardingPricingPageVisited() {
  return getLocalStorageWithLtid(ONBOARDING_PRICING_PAGE_VISITED_KEY);
}

const ONBOARDING_TARGET_FEATURE_KEY = 'onboarding_target_feature_v1';
export function setContentLabOnboardingTargetFeature(
  feature: FeatureName | null
) {
  if (feature && Object.values(FeatureName).includes(feature)) {
    setLocalStorageWithLtid(ONBOARDING_TARGET_FEATURE_KEY, feature);
  } else {
    removeLocalStorageWithLtid(ONBOARDING_TARGET_FEATURE_KEY);
  }
}

export function getContentLabOnboardingTargetFeature() {
  return getLocalStorageWithLtid(ONBOARDING_TARGET_FEATURE_KEY) as
    | FeatureName
    | '';
}

const DO_AND_DONT_SEEN_KEY = 'do_and_dont_seen_v1';

export function setPersistedDoAndDontModalFeatureSeen(featureId: string) {
  setLocalStorageWithLtid(`${DO_AND_DONT_SEEN_KEY}_${featureId}`, true);
}

export function getPersistedDoAndDontModalFeatureSeen(featureId: string) {
  return getLocalStorageWithLtid(`${DO_AND_DONT_SEEN_KEY}_${featureId}`);
}

const CONTENT_LAB_WEB_PAYMENT_FLOW_ID = 'content_lab_web_payment_flow_id';

export function setContentLabWebPaymentFlowId(webPaymentFlowId: string) {
  setLocalStorageWithLtid(CONTENT_LAB_WEB_PAYMENT_FLOW_ID, webPaymentFlowId);
}

export function getContentLabWebPaymentFlowId() {
  return getLocalStorageWithLtid(CONTENT_LAB_WEB_PAYMENT_FLOW_ID);
}

export function removeContentLabWebPaymentFlowId() {
  removeLocalStorageWithLtid(CONTENT_LAB_WEB_PAYMENT_FLOW_ID);
}

export type ContentLabWebPaymentIds = {
  flowId: string;
  processId: string;
  presentationId: string;
  productId: string;
};

const CONTENT_LAB_WEB_PAYMENT_IDS = 'content_lab_web_payment_ids';

export function setContentLabWebPaymentIds(ids: ContentLabWebPaymentIds) {
  setLocalStorageWithLtid(CONTENT_LAB_WEB_PAYMENT_IDS, JSON.stringify(ids));
}

export function getContentLabWebPaymentIds(): ContentLabWebPaymentIds | null {
  const idsAsString = getLocalStorageWithLtid(CONTENT_LAB_WEB_PAYMENT_IDS);
  try {
    return idsAsString ? JSON.parse(idsAsString) : null;
  } catch {
    return null;
  }
}

export function removeContentLabWebPaymentIds() {
  removeLocalStorageWithLtid(CONTENT_LAB_WEB_PAYMENT_IDS);
}

const CONTENT_LAB_PROJECT_SHELF_LAST_OPENED_TIMESTAMP_KEY =
  'content_lab_project_shelf_last_opened_timestamp';

export function setContentLabProjectShelfLastOpenedTimestamp() {
  setLocalStorageWithLtid(
    CONTENT_LAB_PROJECT_SHELF_LAST_OPENED_TIMESTAMP_KEY,
    new Date().toISOString()
  );
}

export function getContentLabProjectShelfLastOpenedTimestamp(): Date | null {
  const lastOpenedTimestamp = getLocalStorageWithLtid(
    CONTENT_LAB_PROJECT_SHELF_LAST_OPENED_TIMESTAMP_KEY
  );
  if (!lastOpenedTimestamp) {
    return null;
  }
  return new Date(lastOpenedTimestamp);
}

export function setDraftProjectId(draftProjectId: string) {
  setLocalStorageWithLtid(DRAFT_PROJECT_ID_KEY, draftProjectId);
}

export function getDraftProjectId() {
  return getLocalStorageWithLtid(DRAFT_PROJECT_ID_KEY);
}

export function removeDraftProjectId() {
  removeLocalStorageWithLtid(DRAFT_PROJECT_ID_KEY);
}

const CAPTIONS_CREDIT_TOOLTIP_SEEN_KEY =
  'captions_source_language_credit_tooltip_seen';

export function setCaptionsSourceLanguageCreditTooltipSeen() {
  setLocalStorageWithLtid(CAPTIONS_CREDIT_TOOLTIP_SEEN_KEY, true);
}

export function getCaptionsSourceLanguageCreditTooltipSeen() {
  return getLocalStorageWithLtid(CAPTIONS_CREDIT_TOOLTIP_SEEN_KEY);
}
